import { CHAINS } from '@/config/chains';
import { strategiesAPI } from '@/services/api/endpoints/strategies';
import { getWalletForChain } from '@/utils/wallets';
import type { ChainId } from '@/stores/slices/chainSlice';
import type { User } from '@/types/api';

interface ChainProbe {
  chainId: ChainId;
  latestTradeAt: number;
}

/**
 * After login, find the chain the user is *actively* on so we can take them
 * straight there instead of defaulting to BSC. Only a chain with a live
 * (current_state === 'active') strategy qualifies — most-recently-traded wins.
 * Returns null when no chain has an active strategy, so the caller keeps the
 * user's current/selected chain (and their intent to onboard a new one).
 *
 * Native balance is not probed here to keep login fast: a live strategy is the
 * signal that a chain is the user's active chain.
 */
export async function detectActiveChain(user: User | null | undefined): Promise<ChainId | null> {
  if (!user) return null;

  const chainsWithWallet = CHAINS.filter((chain) => getWalletForChain(user, chain.id)?.address);
  if (chainsWithWallet.length === 0) return null;

  const probes = await Promise.all(
    chainsWithWallet.map(async (chain): Promise<ChainProbe | null> => {
      try {
        const { strategies } = await strategiesAPI.getActiveRecentStrategies(chain.numericId);
        if (!strategies.some((s) => s.current_state === 'active')) return null;
        const latestTradeAt = strategies.reduce((max, s) => {
          const ts = s.latest_trade_at ? new Date(s.latest_trade_at).getTime() : 0;
          return Math.max(max, ts);
        }, 0);
        return { chainId: chain.id, latestTradeAt };
      } catch {
        return null;
      }
    })
  );

  const active = probes
    .filter((p): p is ChainProbe => p !== null)
    .sort((a, b) => b.latestTradeAt - a.latestTradeAt);
  return active.length > 0 ? active[0].chainId : null;
}
